import {useMemo} from 'react';
import contentListStyle from 'modules/footer/components/Footer/Main/style.module.scss';
import {IFooterDataProps} from '../types';
import {useUserCity} from 'modules/locations/hooks/useUserCity';
import {cityIn} from 'lvovich';
import {IFooterPopularArticle, IFooterPopularSection} from 'modules/footer/models';

interface IStaticFooter {
    code: string;
    itemClass: string;
    linkList: ILinkFooter[];
    title: string;
    path?: string;
}

type TMergedFooterTypes = IFooterPopularArticle & IFooterPopularSection;

interface ILinkFooter extends TMergedFooterTypes {
    code?: string;
}

const STATIC_NAV_ITEMS: IStaticFooter[] = [
    {
        code: 'customers',
        title: 'Покупателям',
        itemClass: contentListStyle.customers,
        linkList: [
            {
                title: 'Контакты',
                url: 'https://simplewine.ru/about/contacts/',
            },
            {
                title: 'Обратная связь',
                url: 'https://simplewine.ru/feedback/',
            },
            {
                title: 'Гарантия',
                url: 'https://simplewine.ru/about/guaranty/',
            },
            {
                title: 'Программа лояльности',
                url: 'https://simplewine.ru/loyalty/',
            },
            {
                title: 'Подарочные сертификаты',
                url: 'https://simplewine.ru/electronic-gift-certificates/',
            },
        ],
        path: '',
    },
    {
        code: 'coop',
        title: 'Сотрудничество',
        itemClass: contentListStyle.cooperation,
        linkList: [
            {
                title: 'Частным клиентам',
                url: 'https://simplewine.ru/prive/',
            },
            {
                title: 'Корпоративным клиентам',
                url: 'https://simplewine.ru/corp/',
            },
            {
                title: 'Карьера',
                url: 'http://careers.simple.ru/?utm_source=site&utm_medium=banner&utm_campaign=Simplewine.ru',
            },
            {
                title: 'Арендодателям',
                url: 'https://simplewine.ru/rent/',
            },
            {
                title: 'Франшиза винных бутиков',
                url: 'https://simple.ru/services/franchise/',
            },
        ],
        path: '',
    },
];

const ABOUT_LINKS = (cityName: string): ILinkFooter[] => [
    {
        title: 'О магазине',
        url: 'https://simplewine.ru/about/',
    },
    {
        code: 'vacancies',
        title: 'Вакансии',
        url: 'http://careers.simple.ru/?utm_source=site&utm_medium=banner&utm_campaign=Simplewine.ru',
    },
    {
        title: 'Как сделать заказ',
        url: 'https://simplewine.ru/about/howto/',
    },
    {
        title: 'Как получить заказ',
        url: 'https://simplewine.ru/about/howto/delivery/',
    },
    {
        title: `Винотеки в ${cityIn(cityName)}`,
        url: '/nashi-vinoteki/',
    },
    {
        title: 'Статьи',
        url: 'https://simplewine.ru/articles/',
    },
];

const PARTITIONS = {
    desktop: [1, 2, 2],
    tablet: [2, 3],
    mobile: [1],
};

const createFooterData = (
    popularSections: IFooterPopularSection[] | undefined,
    popularArticles: IFooterPopularArticle[] | undefined,
    cityName: string,
    layout: 'desktop' | 'tablet',
): IStaticFooter[] => {
    const baseItems = [
        {
            code: 'popular',
            title: 'Популярные разделы',
            itemClass: contentListStyle.popular,
            linkList: popularSections || [],
        },
        {
            code: 'articles',
            title: 'Популярные статьи',
            itemClass: contentListStyle.articles,
            linkList: popularArticles || [],
        },
        ...STATIC_NAV_ITEMS,
        {
            code: 'about',
            title: 'О simplewine',
            itemClass: contentListStyle.about,
            linkList: ABOUT_LINKS(cityName),
        },
    ];

    return 'desktop' === layout
        ? [baseItems[0], STATIC_NAV_ITEMS[0], STATIC_NAV_ITEMS[1], baseItems[1], baseItems[4]]
        : [baseItems[0], baseItems[1], STATIC_NAV_ITEMS[0], STATIC_NAV_ITEMS[1], baseItems[4]];
};

const partitionData = (data: IStaticFooter[], partitionSizes: number[]) => {
    return partitionSizes.map((_, i, sizes) =>
        data.slice(
            sizes.slice(0, i).reduce((a, b) => a + b, 0),
            sizes.slice(0, i + 1).reduce((a, b) => a + b, 0),
        ),
    );
};

export const useContentList = ({
    popularArticles,
    popularSections,
}: IFooterDataProps): {
    desktop: IStaticFooter[][];
    tablet: IStaticFooter[][];
} => {
    const city = useUserCity();
    const cityName = city?.title || '';

    const dataDesktop = useMemo(
        () => createFooterData(popularSections, popularArticles, cityName, 'desktop'),
        [cityName, popularArticles, popularSections],
    );

    const dataTablet = useMemo(
        () => createFooterData(popularSections, popularArticles, cityName, 'tablet'),
        [cityName, popularArticles, popularSections],
    );

    return useMemo(
        () => ({
            desktop: partitionData(dataDesktop, PARTITIONS.desktop),
            tablet: partitionData(dataTablet, PARTITIONS.tablet),
        }),
        [dataDesktop, dataTablet],
    );
};
